import React, { useState, useEffect, useRef, useCallback } from 'react';
import { sendMessage, escalateToAgent, getLatestConversation } from '../utils/api';
import '../styles/SupportWidget.css';

const ROLE_LABELS = {
  employee: 'Employee',
  employer_hr: 'Employer / HR',
  broker: 'Broker / Ops',
};

const MAX_FILE_SIZE = 50 * 1024 * 1024;
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
const VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];

const DEFAULT_QUICK_REPLIES = {
  employee: [
    'How many claims do I have?',
    'What is the status of my latest claim?',
    'List all members covered under my policies',
    'Show my e-card details',
    'My policy details with sum insured',
    'My TPA details and Relationship Manager (RM) contact',
    'Nearby network hospitals under my policy',
  ],
  employer_hr: [
    'How many claims are there under my policies?',
    'Show settled vs pending claims',
    'List employees covered under my policies',
    'Claim statistics by status',
  ],
  broker: [
    'How many claims are there overall?',
    'Claims by status',
    'List network hospitals',
  ],
};

function safeGet(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function safeSet(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch (e) {
    /* ignore quota / blocked storage */
  }
}

function safeRemove(key) {
  try {
    window.localStorage.removeItem(key);
  } catch (e) {
    /* ignore */
  }
}

export default function SupportWidget({ userRole, userId, orgId, token, quickReplies, greeting }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const conversationStorageKey = `cbid:${userRole || ''}:${userId || orgId || ''}`;
  const [conversationId, setConversationId] = useState(() => safeGet(conversationStorageKey) || null);
  const endRef = useRef(null);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const quickRepliesList = Array.isArray(quickReplies) && quickReplies.length
    ? quickReplies
    : (DEFAULT_QUICK_REPLIES[userRole] || []);

  const greetingMsg = {
    id: -1,
    type: 'bot',
    content:
      (greeting || `Hello! I'm your benefits support assistant for ${ROLE_LABELS[userRole]}.\n\n` +
        'I can help with:\n' +
        '- Benefits & claims\n' +
        '- E-cards\n' +
        '- Data uploads\n' +
        '- General questions\n\n' +
        "Describe your issue, or attach a screenshot/video and I'll analyze it."),
    timestamp: new Date().toISOString(),
    isGreeting: true,
  };

  const scrollToBottom = useCallback(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (!isOpen || messages.length > 0) return;
    let cancelled = false;
    const cached = safeGet(`${conversationStorageKey}:messages`);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length) {
          setMessages([greetingMsg, ...parsed.map((m) => ({
            ...m,
            id: Date.now() + Math.random(),
            type: m.type === 'assistant' ? 'bot' : m.type === 'user' ? 'user' : m.type,
            content: formatBotMessage(m.content),
            actions: m.actions || (m.metadata && m.metadata.actions) || [],
            category: m.category || (m.metadata ? m.metadata.category : undefined),
          }))]);
          return;
        }
      } catch (e) {
        /* fall through to server fetch */
      }
    }
    (async () => {
      try {
        const latest = await getLatestConversation({ role: userRole, userId, orgId, token });
        if (cancelled) return;
        if (latest.conversation_id) {
          setConversationId(latest.conversation_id);
          safeSet(conversationStorageKey, latest.conversation_id);
          const restored = (latest.messages || [])
            .filter((m) => m && m.content)
            .map((m) => ({
              id: Date.now() + Math.random(),
              type: m.type === 'assistant' ? 'bot' : m.type === 'user' ? 'user' : m.type,
              content: formatBotMessage(m.content),
              actions: m.metadata && m.metadata.actions ? m.metadata.actions : [],
              category: m.metadata ? m.metadata.category : undefined,
              timestamp: m.created_at,
            }));
          if (restored.length) {
            setMessages([greetingMsg, ...restored]);
            safeSet(`${conversationStorageKey}:messages`, JSON.stringify(restored));
          } else {
            setMessages([greetingMsg]);
          }
        } else {
          setMessages([greetingMsg]);
        }
      } catch (e) {
        if (!cancelled) setMessages([greetingMsg]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, userRole, userId, orgId, token, conversationStorageKey]);

  const handleFiles = async (files) => {
    const processed = [];
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        processed.push({ error: `${file.name} exceeds 50MB` });
        continue;
      }
      if (IMAGE_TYPES.includes(file.type) || VIDEO_TYPES.includes(file.type)) {
        processed.push({ file, type: IMAGE_TYPES.includes(file.type) ? 'image' : 'video', name: file.name, preview: URL.createObjectURL(file) });
      } else {
        processed.push({ error: `Unsupported file type: ${file.type}` });
      }
    }
    setAttachments((prev) => [...prev, ...processed]);
  };

  const onSubmit = async (e) => {
    e?.preventDefault();
    const validAttachments = attachments.filter((a) => !a.error);
    if ((!input.trim() && validAttachments.length === 0) || isLoading) return;
    await submitPrompt(input.trim(), validAttachments);
  };

  const onQuickReply = async (prompt) => {
    if (isLoading) return;
    await submitPrompt(prompt, []);
  };

  const submitPrompt = async (text, validAttachments) => {
    const userMsg = {
      id: Date.now(),
      type: 'user',
      content: text,
      attachments: validAttachments,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setAttachments([]);
    setIsLoading(true);

    try {
      const res = await sendMessage({
        message: text,
        role: userRole,
        userId,
        orgId,
        token,
        conversationId,
        attachments: validAttachments.map((a) => a.file),
      });
      setConversationId(res.conversation_id);
      safeSet(conversationStorageKey, res.conversation_id);
      const cacheEntry = [
        { type: 'user', content: userMsg.content, timestamp: userMsg.timestamp },
        {
          type: 'assistant',
          content: res.message,
          actions: res.actions || [],
          category: res.category,
          timestamp: new Date().toISOString(),
        },
      ];
      setMessages((prev) => {
        const base = prev.filter((m) => !m.isGreeting);
        safeSet(`${conversationStorageKey}:messages`, JSON.stringify(base.concat(cacheEntry)));
        return [...prev, {
          id: Date.now() + 1,
          type: 'bot',
          content: formatBotMessage(res.message),
          actions: res.actions || [],
          category: res.category,
          timestamp: new Date().toISOString(),
        }];
      });
    } catch {
      setMessages((prev) => [
        ...prev,
        { id: Date.now() + 1, type: 'bot', content: 'Sorry, I hit an error. Please try again.', isError: true, timestamp: new Date().toISOString() },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const onEscalate = async () => {
    if (!conversationId) return;
    setIsLoading(true);
    try {
      const res = await escalateToAgent(conversationId, token);
      setMessages((prev) => [
        ...prev,
        { id: Date.now(), type: 'bot', content: `Your issue has been escalated. Ticket #${res.ticket_id}. Our team will contact you.`, isEscalation: true, timestamp: new Date().toISOString() },
      ]);
    } catch {
      setMessages((prev) => [...prev, { id: Date.now(), type: 'bot', content: 'Failed to escalate. Please email support.', isError: true, timestamp: new Date().toISOString() }]);
    } finally {
      setIsLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <div className="sw-container">
      {!isOpen && (
        <button className="sw-trigger" onClick={() => setIsOpen(true)} aria-label="Open support">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </button>
      )}

      {isOpen && (
        <div className="sw-window">
          <div className="sw-header">
            <div>
              <h3>Benefits Support</h3>
              <span className="sw-badge">{ROLE_LABELS[userRole]}</span>
            </div>
            <button className="sw-close" onClick={() => setIsOpen(false)} aria-label="Close">×</button>
          </div>

          <div className="sw-messages">
            {messages.map((m) => (
              <div key={m.id} className={`sw-msg sw-msg-${m.type}`}>
                {m.type === 'bot' && <div className="sw-avatar">AI</div>}
                <div className={`sw-bubble ${m.isError ? 'sw-error' : ''} ${m.isEscalation ? 'sw-escalation' : ''}`}>
                  <div dangerouslySetInnerHTML={{ __html: typeof m.content === 'string' ? m.content : '' }} />
                  {Array.isArray(m.actions) && m.actions.length > 0 && (
                    <div className="sw-actions">
                      {m.actions.filter((a) => a && a.url).map((a, i) => (
                        <a
                          key={i}
                          href={a.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-block', margin: '6px 8px 0 0', padding: '6px 12px',
                            background: '#667eea', color: '#fff', borderRadius: '16px',
                            textDecoration: 'none', fontSize: '12px',
                          }}
                        >
                          {a.label || a.url}
                        </a>
                      ))}
                    </div>
                  )}
                  {m.attachments?.map((a, i) =>
                    a.type === 'image' ? (
                      <img key={i} src={a.preview} alt={a.name} className="sw-preview" />
                    ) : (
                      <video key={i} src={a.preview} controls className="sw-preview" />
                    )
                  )}
                  {m.category && <div className={`sw-cat sw-cat-${m.category}`}>{m.category}</div>}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="sw-msg sw-msg-bot">
                <div className="sw-avatar">AI</div>
                <div className="sw-bubble sw-typing"><span></span><span></span><span></span></div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {attachments.length > 0 && (
            <div className="sw-previews">
              {attachments.map((a, i) => (
                <div key={i} className="sw-preview-item">
                  {a.error ? <span className="sw-perror">{a.error}</span> : (
                    <>
                      {a.type === 'image' && <img src={a.preview} alt={a.name} />}
                      {a.type === 'video' && <video src={a.preview} />}
                      <button className="sw-remove" onClick={() => setAttachments((p) => p.filter((_, x) => x !== i))}>×</button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          {quickRepliesList.length > 0 && messages.filter((m) => !m.isGreeting).length === 0 && (
            <div className="sw-quickreplies">
              {quickRepliesList.map((q, i) => (
                <button key={i} className="sw-quickreply" onClick={() => onQuickReply(q)}>
                  {q}
                </button>
              ))}
            </div>
          )}

          <form className="sw-input" onSubmit={onSubmit}>
            <button type="button" className="sw-btn" onClick={() => imageInputRef.current?.click()} title="Screenshot">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><line x1="21" y1="15" x2="15" y2="9"/></svg>
            </button>
            <button type="button" className="sw-btn" onClick={() => videoInputRef.current?.click()} title="Video">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 7l-7 5 7 5V7z"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
            </button>
            <textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKeyDown} placeholder="Describe your issue..." rows={1} />
            <button type="submit" className="sw-send" disabled={isLoading || (!input.trim() && attachments.length === 0)}>➤</button>
          </form>

          <div className="sw-footer">
            {quickRepliesList.length > 0 && (
              <button className={`sw-menu-toggle ${menuOpen ? 'sw-active' : ''}`} onClick={() => setMenuOpen((v) => !v)}>
                ☰ Options
              </button>
            )}
            <button className="sw-escalate" onClick={onEscalate}>Escalate to Human Agent</button>
          </div>

          {menuOpen && quickRepliesList.length > 0 && (
            <div className="sw-menu-sheet">
              {quickRepliesList.map((q, i) => (
                <button key={i} className="sw-drawer-item" onClick={() => { setMenuOpen(false); onQuickReply(q); }}>
                  {q}
                </button>
              ))}
            </div>
          )}

          <input ref={imageInputRef} type="file" accept="image/*" multiple hidden onChange={(e) => handleFiles(Array.from(e.target.files))} />
          <input ref={videoInputRef} type="file" accept="video/*" multiple hidden onChange={(e) => handleFiles(Array.from(e.target.files))} />
        </div>
      )}
    </div>
  );
}

function formatBotMessage(text) {
  const raw = (text || '').toString();
  // Normalise any literal HTML the model produced into plain structure first,
  // so we never render visible <br>/<li> tags.
  let s = raw
    .replace(/<\/?(?:ul|ol)\s*>/gi, '\n')
    .replace(/<\/?li\s*>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/?(?:p|div)\s*>/gi, '\n');
  // Escape HTML so the only markup is what we generate below.
  s = s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  // Inline styles
  s = s
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/(https?:\/\/[^\s<]+)/g, (url) => `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`);

  const lines = s.split('\n').map((l) => l.replace(/\s+$/g, ''));
  const out = [];
  let list = null; // 'ul' | 'ol'
  let br = false; // whether a <br/> separator should precede the next item
  const push = (html) => {
    if (br) out.push('<br/>');
    out.push(html);
    br = false;
  };
  const flush = () => {
    if (list) { push(`</${list}>`); list = null; }
  };
  for (const line of lines) {
    const bullet = line.match(/^\s*[-*•]\s+(.*)$/);
    const ordered = line.match(/^\s*\d+[.)]\s+(.*)$/);
    const kind = bullet ? 'ul' : (ordered ? 'ol' : null);
    if (kind) {
      if (list !== kind) { flush(); list = kind; push(`<${kind}>`); }
      push(`<li>${bullet ? bullet[1] : ordered[1]}</li>`);
      continue;
    }
    flush();
    if (line.trim()) { push(line); br = true; }
  }
  flush();
  return out.join('');
}
